import * as THREE from "three";
import {
	getTerrainSlopeAtPoint,
	type SiteConditions,
} from "../../domain/foundation/siteConditions";
import { getGroundReliefAt } from "./SceneEnvironment.helpers";

/** One texture, one ground mesh, one instanced stone mesh on every quality tier. */
export const LANDSCAPE_BUDGET = {
	drawables: 2,
	stones: 7,
	textureSize: 128,
} as const;

/**
 * How far the gravel pad floats over the terrain. Anything laid ON the
 * gravel (the dusk sockel light pools) lifts from this, not from the raw
 * ground, or the pad paints over it.
 */
export const GRAVEL_LIFT_M = 0.018;

export function createGravelGeometry(
	footprint: { width: number; length: number },
	groundY: number,
	site: SiteConditions,
) {
	const positions: number[] = [];
	const uv: number[] = [];
	const indices: number[] = [];
	const halfW = footprint.width / 2 + 0.75;
	const halfL = footprint.length / 2 + 0.75;
	const add = (x: number, z: number) => {
		positions.push(
			x,
			groundY +
				getTerrainSlopeAtPoint(site, x, z) +
				getGroundReliefAt(x, z) +
				GRAVEL_LIFT_M,
			z,
		);
		uv.push(x / 0.65, z / 0.65);
	};
	add(0, 0);
	// An irregular rounded rectangle, with physical UVs independent of project size.
	for (let i = 0; i < 32; i++) {
		const a = (i / 32) * Math.PI * 2;
		const c = Math.cos(a);
		const s = Math.sin(a);
		const ripple = 1 + Math.sin(i * 2.4) * 0.025;
		add(
			Math.sign(c) * Math.sqrt(Math.abs(c)) * halfW * ripple,
			Math.sign(s) * Math.sqrt(Math.abs(s)) * halfL * ripple,
		);
	}
	for (let i = 0; i < 32; i++) indices.push(0, ((i + 1) % 32) + 1, i + 1);
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
	geometry.setIndex(indices);
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
}

export function createGravelTexture() {
	const size = LANDSCAPE_BUDGET.textureSize;
	const data = new Uint8Array(size * size * 4);
	// Tileable deterministic fine aggregate. No external request or alpha overdraw.
	for (let y = 0; y < size; y++)
		for (let x = 0; x < size; x++) {
			let h =
				Math.imul((x >> 1) + 1, 73856093) ^ Math.imul((y >> 1) + 1, 19349663);
			h = Math.imul(h ^ (h >>> 13), 1274126177);
			const tone = (h >>> 0) / 4294967296;
			const shade = 0.82 + tone * 0.18;
			const i = (y * size + x) * 4;
			data[i] = Math.round(134 * shade);
			data[i + 1] = Math.round(129 * shade);
			data[i + 2] = Math.round(118 * shade);
			data[i + 3] = 255;
		}
	const texture = new THREE.DataTexture(data, size, size);
	texture.colorSpace = THREE.SRGBColorSpace;
	texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
	texture.magFilter = THREE.LinearFilter;
	texture.minFilter = THREE.LinearMipmapLinearFilter;
	texture.generateMipmaps = true;
	texture.needsUpdate = true;
	return texture;
}

export function landscapeStoneMatrices(
	footprint: { width: number; length: number },
	groundY: number,
	site: SiteConditions,
) {
	const halfW = footprint.width / 2;
	const halfL = footprint.length / 2;
	// Two asymmetric groups outside the kept-clear footprint and entry approach.
	const stones = [
		[-halfW - 1.05, -halfL - 0.1, 0.55, 0.32, 0.42],
		[-halfW - 0.75, -halfL - 0.65, 0.29, 0.2, 0.31],
		[-halfW - 1.5, -halfL + 0.5, 0.21, 0.13, 0.25],
		[halfW + 0.95, -halfL - 0.45, 0.42, 0.25, 0.32],
		[halfW + 1.4, -halfL - 0.15, 0.22, 0.14, 0.2],
		[halfW + 0.7, -halfL - 1, 0.27, 0.17, 0.3],
		[-halfW - 1.35, -halfL - 1, 0.16, 0.1, 0.18],
	];
	return stones.map(([x, z, sx, sy, sz], i) =>
		new THREE.Matrix4().compose(
			new THREE.Vector3(
				x,
				groundY +
					getTerrainSlopeAtPoint(site, x, z) +
					getGroundReliefAt(x, z) +
					sy * 0.38,
				z,
			),
			new THREE.Quaternion().setFromEuler(
				new THREE.Euler(0.12 * i, 0.72 * i, 0.15),
			),
			new THREE.Vector3(sx, sy, sz),
		),
	);
}
